import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BellIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { formatDistanceToNow } from 'date-fns';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DocumentInstanceRead } from '@/services/api/generated/hipaaESignService';

import { HipaaESignStatusBadge } from './HipaaESignStatusBadge';
import { HipaaReminderModal } from './HipaaReminderModal';

/** Type for detailed agreement info returned from Adobe Sign */
interface DetailedAgreementInfo {
  external_url?: string;
  signer_info?: Array<{
    status: 'SENT' | 'SIGNED';
    email: string | null;
    name: string | null;
  }>;
}

interface HipaaESignStatusStripProps {
  status: DocumentInstanceRead;
  onRefresh: () => void;
  /** Callback to send a reminder with optional phone override and message */
  onSendReminder: (options: {
    phoneNumber?: string;
    smsMessage?: string;
  }) => Promise<void>;
  /** Callback to void/cancel the signature request */
  onVoid?: () => Promise<void>;
  isRefreshing?: boolean;
  isSendingReminder?: boolean;
  isVoiding?: boolean;
  /** Whether the request can be voided (only when pending) */
  canVoid?: boolean;
  /** Client name for display in reminder modal */
  clientName?: string | null;
  /** Firm name for SMS message template */
  firmName?: string | null;
  className?: string;
}

export const HipaaESignStatusStrip: React.FC<HipaaESignStatusStripProps> = ({
  status,
  onRefresh,
  onSendReminder,
  onVoid,
  isRefreshing = false,
  isSendingReminder = false,
  isVoiding = false,
  canVoid = false,
  clientName,
  firmName,
  className,
}) => {
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);

  const isPending = status.status === 'SENT';
  const isSigned = status.status === 'SIGNED' || status.status === 'COMPLETED';

  const createdAt = status.created_at
    ? formatDistanceToNow(new Date(status.created_at), { addSuffix: true })
    : null;

  // Extract signer info from detailed_agreement_info
  const detailedInfo = status.detailed_agreement_info as
    | DetailedAgreementInfo
    | undefined;
  const signerInfo = detailedInfo?.signer_info?.[0];
  const adobeSignUrl = detailedInfo?.external_url;

  const handleReminderSubmit = async (params: {
    phoneNumber: string;
    smsMessage?: string;
  }) => {
    await onSendReminder({
      phoneNumber: params.phoneNumber,
      smsMessage: params.smsMessage,
    });
    setShowReminderModal(false);
  };

  const handleVoidConfirm = async () => {
    if (onVoid) {
      try {
        await onVoid();
        setShowVoidDialog(false);
      } catch {
        // Keep dialog open on error so user can retry
      }
    }
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-2 px-4 py-3 rounded-lg border',
          isSigned
            ? 'bg-green-50 border-green-200'
            : 'bg-amber-50 border-amber-200',
          className,
        )}
      >
        {/* Top row: Badge and timestamp */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-xs font-medium text-gray-600 flex-shrink-0">
              HIPAA E-Sign:
            </span>
            <HipaaESignStatusBadge status={status.status} size="sm" />
            {createdAt && (
              <span className="text-xs text-gray-500 truncate">
                Sent {createdAt}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Refresh Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="h-7 w-7 p-0"
                >
                  {isRefreshing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Refresh status from Adobe Sign
              </TooltipContent>
            </Tooltip>

            {/* Remind Button (only when pending) */}
            {isPending && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowReminderModal(true)}
                    disabled={isSendingReminder}
                    className="h-7 px-2 gap-1 text-xs"
                  >
                    {isSendingReminder ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <BellIcon className="h-3.5 w-3.5" />
                    )}
                    <span className="hidden sm:inline">Remind</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Send reminder to signer
                </TooltipContent>
              </Tooltip>
            )}

            {/* Void Button (only when pending and callback provided) */}
            {canVoid && onVoid && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVoidDialog(true)}
                    disabled={isVoiding}
                    className="h-7 px-2 gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {isVoiding ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XCircleIcon className="h-3.5 w-3.5" />
                    )}
                    <span className="hidden sm:inline">Void</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Cancel this signature request
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Bottom row: Signer info and Adobe Sign link */}
        {(signerInfo || adobeSignUrl) && (
          <div className="flex items-center justify-between gap-3 text-xs text-gray-600">
            {/* Signer info */}
            {signerInfo && (
              <div className="flex items-center gap-1 min-w-0 truncate">
                <span className="text-gray-500">To:</span>
                {signerInfo.name && (
                  <span className="font-medium">{signerInfo.name}</span>
                )}
                {signerInfo.email && (
                  <span className="text-gray-500 truncate">
                    {signerInfo.name
                      ? `<${signerInfo.email}>`
                      : signerInfo.email}
                  </span>
                )}
              </div>
            )}

            {/* Adobe Sign link */}
            {adobeSignUrl && (
              <a
                href={adobeSignUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors flex-shrink-0"
              >
                <span>View in Adobe Sign</span>
                <ArrowTopRightOnSquareIcon className="h-3 w-3" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Reminder Modal */}
      <HipaaReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSubmit={handleReminderSubmit}
        isSubmitting={isSendingReminder}
        originalEmail={signerInfo?.email}
        clientName={clientName}
        firmName={firmName}
      />

      {/* Void Confirmation Dialog */}
      <AlertDialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Signature Request?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to void this HIPAA Authorization request?
              The signer will no longer be able to sign, and this action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isVoiding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidConfirm}
              disabled={isVoiding}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isVoiding ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Voiding...
                </>
              ) : (
                'Void Request'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
